/**
 * Deterministic GLSL noise used by the sky / cloud / volumetric shaders.
 * Hash-based (no texture lookups) so it works identically on every GPU and
 * needs no assets. Owner: lighting.
 */

export const NOISE_GLSL = /* glsl */ `
#ifndef AAA_NOISE_INCLUDED
#define AAA_NOISE_INCLUDED

float aaaHash11( float p ) {
  p = fract( p * 0.1031 );
  p *= p + 33.33;
  p *= p + p;
  return fract( p );
}

vec3 aaaHash33( vec3 p ) {
  // sin-free hash — a sin() based hash costs ~3x more and the cloud march
  // evaluates this several thousand times per pixel.
  p = fract( p * vec3( 0.1031, 0.1030, 0.0973 ) );
  p += dot( p, p.yxz + 33.33 );
  return -1.0 + 2.0 * fract( ( p.xxy + p.yxx ) * p.zyx );
}

float aaaHash13( vec3 p ) {
  p = fract( p * 0.1031 );
  p += dot( p, p.zyx + 31.32 );
  return fract( ( p.x + p.y ) * p.z );
}

/** Gradient (Perlin-style) noise, roughly [-1,1]. */
float aaaNoise3( vec3 x ) {
  vec3 i = floor( x );
  vec3 f = fract( x );
  vec3 u = f * f * ( 3.0 - 2.0 * f );

  return mix( mix( mix( dot( aaaHash33( i + vec3( 0, 0, 0 ) ), f - vec3( 0, 0, 0 ) ),
                        dot( aaaHash33( i + vec3( 1, 0, 0 ) ), f - vec3( 1, 0, 0 ) ), u.x ),
                   mix( dot( aaaHash33( i + vec3( 0, 1, 0 ) ), f - vec3( 0, 1, 0 ) ),
                        dot( aaaHash33( i + vec3( 1, 1, 0 ) ), f - vec3( 1, 1, 0 ) ), u.x ), u.y ),
              mix( mix( dot( aaaHash33( i + vec3( 0, 0, 1 ) ), f - vec3( 0, 0, 1 ) ),
                        dot( aaaHash33( i + vec3( 1, 0, 1 ) ), f - vec3( 1, 0, 1 ) ), u.x ),
                   mix( dot( aaaHash33( i + vec3( 0, 1, 1 ) ), f - vec3( 0, 1, 1 ) ),
                        dot( aaaHash33( i + vec3( 1, 1, 1 ) ), f - vec3( 1, 1, 1 ) ), u.x ), u.y ), u.z );
}

/** Worley / cellular F1 in [0,1]; small values = cell centres. */
float aaaWorley3( vec3 p ) {
  vec3 id = floor( p );
  vec3 fd = fract( p );
  float minDist = 1.0;
  for ( int z = -1; z <= 1; z ++ ) {
    for ( int y = -1; y <= 1; y ++ ) {
      for ( int x = -1; x <= 1; x ++ ) {
        vec3 o = vec3( float( x ), float( y ), float( z ) );
        vec3 h = aaaHash33( id + o ) * 0.5 + 0.5;
        vec3 d = o + h - fd;
        minDist = min( minDist, dot( d, d ) );
      }
    }
  }
  return sqrt( minDist );
}

const mat3 AAA_ROT = mat3(
  0.00,  0.80,  0.60,
 -0.80,  0.36, -0.48,
 -0.60, -0.48,  0.64 );

float aaaFbm3( vec3 p, const int octaves ) {
  float f = 0.0;
  float a = 0.5;
  for ( int i = 0; i < octaves; i ++ ) {
    f += a * aaaNoise3( p );
    p = AAA_ROT * p * 2.02;
    a *= 0.5;
  }
  return f * ( 1.0 / ( 1.0 - pow( 0.5, float( octaves ) ) ) ) * 0.5;
}

/**
 * Billowy detail field — cauliflower edges without paying for a 27-cell
 * Worley lookup at every one of the thousands of samples a cloud march takes.
 * abs() folds the gradient noise into lobes; three octaves reads as curd.
 */
float aaaBillow( vec3 p, float freq ) {
  vec3 q = p * freq;
  float a = 1.0 - abs( aaaNoise3( q ) );
  float b = 1.0 - abs( aaaNoise3( AAA_ROT * q * 2.03 ) );
  float c = 1.0 - abs( aaaNoise3( AAA_ROT * q * 4.11 + 7.3 ) );
  return clamp( a * 0.55 + b * 0.3 + c * 0.15, 0.0, 1.0 );
}

/**
 * Curl-style domain warp. A true finite-difference curl needs six noise taps;
 * three decorrelated taps give the same swirling, non-axis-aligned distortion
 * for half the cost, which is what the cloud shapes actually need.
 */
vec3 aaaCurl( vec3 p ) {
  return vec3(
    aaaNoise3( p ),
    aaaNoise3( AAA_ROT * p + 19.19 ),
    aaaNoise3( AAA_ROT * AAA_ROT * p - 7.77 ) );
}

/** Interleaved gradient noise — free per-pixel dither, stable and deterministic. */
float aaaIGN( vec2 p ) {
  return fract( 52.9829189 * fract( dot( p, vec2( 0.06711056, 0.00583715 ) ) ) );
}

/** Vogel disk — even circular sample distribution. */
vec2 aaaVogel( int i, int count, float phi ) {
  float r = sqrt( ( float( i ) + 0.5 ) / float( count ) );
  float theta = float( i ) * 2.399963229728653 + phi;
  return vec2( cos( theta ), sin( theta ) ) * r;
}

#endif
`;
